import { normalizeMemberLoginEmail } from "./memberCredentialActions";
import {
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";

export type MemberLoginCollisionAction =
  | "save"
  | "approveWebsiteApplication"
  | "sendCredentials"
  | "downloadCredentials";

export type MemberLoginAvailabilityLike = {
  code?: string | null;
  checkedLoginEmail?: string | null;
  suggestedLoginEmail?: string | null;
};

export type MemberLoginAvailabilityDecision =
  | {
      status: "available";
      checkedLoginEmail: string;
    }
  | {
      status: "collision";
      checkedLoginEmail: string;
      suggestedLoginEmail: string;
    }
  | {
      status: "stale";
    }
  | {
      status: "invalid";
      message: string;
    };

export const memberLoginAvailabilitySupportError =
  `We could not find another sign-in email that this member can use, so we stopped. Email ${STAFF_SUPPORT_EMAIL} for help.`;

export const memberLoginAvailabilityStaleError =
  "The Member Administrator Contact email changed while we were working, so we stopped. Check the email and try again.";

export const getMemberLoginAvailabilityDecision = ({
  availability,
  expectedLoginEmail,
  queryError,
}: {
  availability?: MemberLoginAvailabilityLike | null;
  expectedLoginEmail: string;
  queryError?: Error;
}): MemberLoginAvailabilityDecision => {
  if (queryError) throw queryError;

  const normalizedExpectedLoginEmail =
    normalizeMemberLoginEmail(expectedLoginEmail);
  const normalizedCheckedLoginEmail = normalizeMemberLoginEmail(
    availability?.checkedLoginEmail
  );

  if (!normalizedExpectedLoginEmail || !normalizedCheckedLoginEmail) {
    return {
      status: "invalid",
      message: memberLoginAvailabilitySupportError,
    };
  }

  if (normalizedCheckedLoginEmail !== normalizedExpectedLoginEmail) {
    return { status: "stale" };
  }

  if (availability?.code === "AVAILABLE") {
    return {
      status: "available",
      checkedLoginEmail: normalizedCheckedLoginEmail,
    };
  }

  if (availability?.code === "LOGIN_EMAIL_IN_USE") {
    const exactSuggestedLoginEmail = availability.suggestedLoginEmail || "";
    const normalizedSuggestedLoginEmail = normalizeMemberLoginEmail(
      exactSuggestedLoginEmail
    );

    if (
      !normalizedSuggestedLoginEmail ||
      normalizedSuggestedLoginEmail === normalizedCheckedLoginEmail
    ) {
      return {
        status: "invalid",
        message: memberLoginAvailabilitySupportError,
      };
    }

    return {
      status: "collision",
      checkedLoginEmail: normalizedCheckedLoginEmail,
      // Keep the server value opaque. The caller displays it and sends the
      // same value to the dedicated login-change mutation.
      suggestedLoginEmail: exactSuggestedLoginEmail,
    };
  }

  return {
    status: "invalid",
    message: memberLoginAvailabilitySupportError,
  };
};

export type MemberLoginChangeResultLike = {
  adminConsoleLoginEmailChangedSuccessfully?: boolean | null;
  email?: string | null;
  failureReason?: string | null;
  loginAvailability?: MemberLoginAvailabilityLike | null;
};

const memberLoginChangeUnconfirmedError =
  `The sign-in email was not changed. Refresh the page and check the Member Administrator Contact email before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;

export const getMemberLoginChangeAvailabilityDecision = ({
  changeResult,
  expectedLoginEmail,
}: {
  changeResult?: MemberLoginChangeResultLike | null;
  expectedLoginEmail: string;
}): MemberLoginAvailabilityDecision => {
  const availabilityDecision = getMemberLoginAvailabilityDecision({
    availability: changeResult?.loginAvailability,
    expectedLoginEmail,
  });

  // A collision can occur after the preflight check. Keep its validated
  // suggestion, and never use a stale response to resolve another email.
  if (
    availabilityDecision.status === "collision" ||
    availabilityDecision.status === "stale"
  ) {
    return availabilityDecision;
  }

  if (!changeResult?.adminConsoleLoginEmailChangedSuccessfully) {
    return {
      status: "invalid",
      message: changeResult?.failureReason || memberLoginChangeUnconfirmedError,
    };
  }

  return availabilityDecision;
};

/**
 * The dedicated login mutation is a commit boundary. Its success Boolean,
 * returned email, and typed availability result must all confirm the exact
 * alternative before the original action can continue.
 */
export const getConfirmedMemberLoginEmail = ({
  changeResult,
  expectedLoginEmail,
}: {
  changeResult?: MemberLoginChangeResultLike | null;
  expectedLoginEmail: string;
}): string => {
  if (!changeResult?.adminConsoleLoginEmailChangedSuccessfully) {
    // failureReason is server text for staff and is shown as it is.
    throw new UserFacingError(
      changeResult?.failureReason || memberLoginChangeUnconfirmedError
    );
  }

  const normalizedExpectedLoginEmail =
    normalizeMemberLoginEmail(expectedLoginEmail);
  const normalizedReturnedLoginEmail = normalizeMemberLoginEmail(
    changeResult.email
  );
  const availabilityDecision = getMemberLoginAvailabilityDecision({
    availability: changeResult.loginAvailability,
    expectedLoginEmail: normalizedExpectedLoginEmail,
  });

  if (
    !normalizedExpectedLoginEmail ||
    normalizedReturnedLoginEmail !== normalizedExpectedLoginEmail ||
    availabilityDecision.status !== "available"
  ) {
    // The login change may have committed, so staff check before a retry.
    throw new UserFacingError(
      "The sign-in email did not save as expected, so we stopped. Refresh the page, check the Member Administrator Contact email, and try again."
    );
  }

  return normalizedReturnedLoginEmail;
};

export type MemberLoginCollisionSingleFlight = {
  isRunning: () => boolean;
  run: <T>(task: () => Promise<T>) => Promise<
    | { started: false }
    | {
        started: true;
        value: T;
      }
  >;
};

/**
 * Keep one alternative-login resolution in flight. Repeated button events
 * are ignored instead of starting a second login change or continuation.
 */
export const createMemberLoginCollisionSingleFlight =
  (): MemberLoginCollisionSingleFlight => {
    let running = false;

    return {
      isRunning: () => running,
      run: async <T>(task: () => Promise<T>) => {
        if (running) {
          return { started: false } as const;
        }

        running = true;

        try {
          return {
            started: true,
            value: await task(),
          } as const;
        } finally {
          running = false;
        }
      },
    };
  };

export const continueMemberLoginCollisionAction = async ({
  action,
  onApproveWebsiteApplication,
  onDownloadCredentials,
  onSave,
  onSendCredentials,
}: {
  action: MemberLoginCollisionAction;
  onApproveWebsiteApplication: () => Promise<void>;
  onDownloadCredentials: () => Promise<void>;
  onSave: () => Promise<void>;
  onSendCredentials: () => Promise<void>;
}): Promise<void> => {
  if (action === "save") {
    await onSave();
    return;
  }

  if (action === "approveWebsiteApplication") {
    await onApproveWebsiteApplication();
    return;
  }

  if (action === "downloadCredentials") {
    await onDownloadCredentials();
    return;
  }

  await onSendCredentials();
};
